import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Card, CardContent, CardHeader, CircularProgress, Tab, Tabs, Typography,
    FormControl, InputLabel, Select, MenuItem, Stack,
} from '@mui/material';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';
import { parseCsv } from '../csv';
import TrialsTab from './trials-tab';
import CohortsTab from './cohorts-tab';
import ClientsTab from './clients-tab';
import dayjs from 'dayjs';

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function StrategyPage() {
    const [snapshots, setSnapshots] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [tab, setTab] = useState(0);
    const [csvData, setCsvData] = useState({});
    const fetchWithAuth = useFetch();

    // Load snapshot list
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/snapshots`);
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                if (!cancelled) {
                    setSnapshots(data);
                    if (data.length > 0) setSelectedId(data[0].id);
                }
            } catch (e) {
                console.error('Failed to load snapshots', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [fetchWithAuth]);

    // Load CSV files for selected snapshot
    const loadCsvData = useCallback(async (snapshotId) => {
        if (!snapshotId) return;
        setLoadingData(true);
        setCsvData({});
        try {
            const boards = ['CLIENTS', 'CONTACTS', 'CONTACTS_PRO', 'TESTS'];
            const results = {};
            for (const board of boards) {
                const res = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/snapshots/${snapshotId}/files/${board}`);
                if (res.ok) {
                    const text = await res.text();
                    results[board] = parseCsv(text);
                }
            }
            setCsvData(results);
        } catch (e) {
            console.error('Failed to load CSV data', e);
        } finally {
            setLoadingData(false);
        }
    }, [fetchWithAuth]);

    useEffect(() => {
        if (selectedId) loadCsvData(selectedId);
    }, [selectedId, loadCsvData]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (snapshots.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        Aucun snapshot CRM disponible. Créez-en un depuis la page Snapshots CRM.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const selectedSnapshot = snapshots.find(s => s.id === selectedId);

    return (
        <Box>
            <Card sx={{ mb: 2 }}>
                <CardHeader
                    title="Strategy"
                    subheader="Analyse des données accumulées"
                    action={
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel>Snapshot</InputLabel>
                            <Select
                                value={selectedId}
                                label="Snapshot"
                                onChange={(e) => setSelectedId(e.target.value)}
                            >
                                {snapshots.map(s => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {dayjs(s.created_at).format('YYYY-MM-DD HH:mm')}
                                        {' — '}
                                        {s.files.reduce((sum, f) => sum + (f.row_count || 0), 0)} lignes
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    }
                />
                <CardContent sx={{ pt: 0 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label="Cohortes" />
                        <Tab label="Essais" />
                        <Tab label="Clients" />
                    </Tabs>
                </CardContent>
            </Card>

            {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TabPanel value={tab} index={0}>
                        <CohortsTab
                            contactsRows={csvData.CONTACTS}
                            contactsProRows={csvData.CONTACTS_PRO}
                            clientsRows={csvData.CLIENTS}
                            testsRows={csvData.TESTS}
                        />
                    </TabPanel>
                    <TabPanel value={tab} index={1}>
                        <TrialsTab
                            testsRows={csvData.TESTS}
                            contactsRows={csvData.CONTACTS}
                            contactsProRows={csvData.CONTACTS_PRO}
                            clientsRows={csvData.CLIENTS}
                        />
                    </TabPanel>
                    <TabPanel value={tab} index={2}>
                        <ClientsTab clientsRows={csvData.CLIENTS} />
                    </TabPanel>
                </>
            )}
        </Box>
    );
}
